import React, { useState } from 'react';
import { Flex, Box, IconButton, Icon } from '@chakra-ui/react';

import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

function RevisionAssessmentCarousel({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [activeIndex, setActiveIndex] = useState(0);
  const totalSlides = React.Children.count(children);

  const handlePrevious = () => {
    setActiveIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  const handleNext = () => {
    setActiveIndex((prevIndex) => (prevIndex < totalSlides - 1 ? prevIndex + 1 : prevIndex));
  };

  return (
    <Box width="100%" height="100%">
      <Flex width="100%" alignItems="center" position="relative" height="100%">
        {/* Left navigation button */}
        {totalSlides > 1 ? (
          <IconButton
            onClick={handlePrevious}
            variant="secondary"
            isDisabled={activeIndex === 0}
            aria-label="Previous"
            size="2xs"
            backgroundColor="bg.surface"
            rounded={4}
            height="48px"
            marginRight={3}
          >
            <Icon as={ChevronLeft} fontSize="sm" />
          </IconButton>
        ) : null}

        {/* Content area with padding to avoid button overlap */}
        <Box width="100%" position="relative" height="100%" overflow="hidden">
          {React.Children.map(children, (child, index) => (
            <motion.div
              key={index}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                opacity: index === activeIndex ? 1 : 0,
              }}
              initial={{ x: index < activeIndex ? '-100%' : index > activeIndex ? '100%' : 0 }}
              animate={{ x: index === activeIndex ? 0 : index < activeIndex ? '-100%' : '100%' }}
              exit={{ x: index === activeIndex ? 0 : index < activeIndex ? '100%' : '-100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              {child}
            </motion.div>
          ))}
        </Box>

        {/* Right navigation button */}
        {totalSlides > 1 ? (
          <IconButton
            onClick={handleNext}
            variant="secondary"
            isDisabled={activeIndex === totalSlides - 1}
            aria-label="Next"
            size="2xs"
            backgroundColor="bg.surface"
            rounded={4}
            height="48px"
            marginLeft={3}
          >
            <Icon as={ChevronRight} fontSize="sm" />
          </IconButton>
        ) : null}
      </Flex>
      {totalSlides > 1 ? (
        <Flex w="full" justifyContent="center" gap={1.5}>
          {Array.from(Array(totalSlides).keys()).map((slideIndex, index) => (
            <Box
              w={1}
              h={1}
              key={index}
              backgroundColor={activeIndex === slideIndex ? 'fg.emphasis' : 'fg.disabled'}
              rounded="full"
            ></Box>
          ))}
        </Flex>
      ) : null}
    </Box>
  );
}

export default RevisionAssessmentCarousel;
